/**
 * SelectFieldPopover
 *
 * Filterable popover for editing Select fields inline in the standard table.
 * Options are static (from DocType meta, newline-separated string).
 * Uses Popover + Command (cmdk) with client-side filtering.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { CellSaveState } from '../data-grid/types';

interface SelectFieldPopoverProps {
  /** Newline-separated options string from field meta */
  options: string;
  /** Current field value */
  value: unknown;
  /** Commit new value */
  onCommit: (newValue: string) => void;
  /** Cancel editing */
  onCancel: () => void;
  /** Cell save state */
  saveState: CellSaveState;
}

export function SelectFieldPopover({
  options: rawOptions,
  value,
  onCommit,
  onCancel,
  saveState: _saveState,
}: SelectFieldPopoverProps) {
  const [open, setOpen] = useState(false);
  const committedRef = useRef(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Parse options once — keep empty string as "(None)" for clearing
  const parsedOptions = useMemo(() => {
    if (!rawOptions) return [];
    const items: { label: string; value: string }[] = [];
    const lines = rawOptions.split('\n').map((s) => s.trim());

    for (const line of lines) {
      if (line === '') {
        // Empty option = clear value
        items.push({ label: '(None)', value: '' });
      } else {
        items.push({ label: line, value: line });
      }
    }
    return items;
  }, [rawOptions]);

  // Auto-open on mount
  useEffect(() => {
    const timer = setTimeout(() => setOpen(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSelect = (selectedValue: string) => {
    if (committedRef.current) return;
    committedRef.current = true;
    setOpen(false);

    if (selectedValue !== String(value ?? '')) {
      onCommit(selectedValue);
    } else {
      onCancel();
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen && !committedRef.current) {
      onCancel();
    }
  };

  const currentValue = String(value ?? '');

  return (
    <div ref={anchorRef} className="relative w-full h-full">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className="w-full h-full flex items-center justify-end text-sm truncate [font:inherit] text-inherit">
            {currentValue || <span className="text-muted-foreground">(None)</span>}
          </div>
        </PopoverAnchor>
        {open && (
          <PopoverContent
            className="!w-[340px] max-w-[92vw] p-0 overflow-hidden border-gray-300 dark:border-gray-700 shadow-lg"
            // Anchor is a full-width, right-aligned value — align to its END
            // (right) edge so the dropdown opens under the value, not far left.
            align="end"
            sideOffset={2}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              // Guard: clicking inside the anchor area should NOT dismiss
              // the popover — Radix DismissableLayer treats PopoverAnchor
              // clicks as "outside" and fires onOpenChange(false).
              const target = e.target as Node;
              if (anchorRef.current?.contains(target)) {
                e.preventDefault();
                return;
              }
              if (!committedRef.current) {
                onCancel();
              }
            }}
          >
            <Command>
              <CommandInput
                placeholder="Filter..."
                className="h-8 text-sm"
                autoFocus
              />
              <CommandList className="max-h-[216px]">
                <CommandEmpty>No match</CommandEmpty>
                <CommandGroup>
                  {parsedOptions.map((opt) => (
                    <CommandItem
                      key={opt.value || '__empty__'}
                      value={opt.label}
                      onSelect={() => handleSelect(opt.value)}
                      className="text-sm px-2 py-1.5 min-h-[32px] items-start"
                    >
                      <Check
                        className={cn(
                          'mr-2 mt-0.5 h-3.5 w-3.5 shrink-0',
                          currentValue === opt.value ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className={cn('whitespace-normal break-words leading-snug', opt.value === '' && 'text-muted-foreground italic')}>
                        {opt.label}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}
